import { ArrowBackIos } from '@mui/icons-material';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Loader } from 'components';
import { SelectedLocationContext } from 'contexts/selected-location';
import dayjs from 'dayjs';
import { default as utc } from 'dayjs/plugin/utc';
import { FC, useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  InvoiceEventSaveError,
  addInvoiceItemBillableEvent,
  searchByUnitNumber,
  updateInvoiceItemBillableEvent
} from 'services';
import { getContractForSizeType } from 'services/contracts';
import { BillableEvent, BillableEventType, Contract, Invoice, InvoiceItem, defaultBillableEvent } from 'types';
import { BillableEventView } from '../enums';

dayjs.extend(utc);

const typeToPrettyString = (type: BillableEventType) => {
  switch (type) {
    case BillableEventType.LIFT_OFF:
      return 'Lift Off';
    case BillableEventType.LIFT_ON:
      return 'Lift On';
    case BillableEventType.DIG:
      return 'Dig';
    case BillableEventType.CREDIT:
      return 'Credit';
    case BillableEventType.OTHER:
    default:
      return 'Other';
  }
};

const typesRequiringAssocUnit = [BillableEventType.LIFT_ON];

const EditExistingEventForm: FC<{
  billableEvent: BillableEvent;
  setBillableEvent: React.Dispatch<React.SetStateAction<BillableEvent>>;
  setView: React.Dispatch<React.SetStateAction<BillableEventView>>;
  close: () => void;
  onItemEditSuccess: () => void;
  invoiceItem: InvoiceItem;
}> = ({ billableEvent, setBillableEvent, setView, close, onItemEditSuccess, invoiceItem }) => {
  const [costInputValid, setCostInputValid] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);

  return (
    <form className="mb-3">
      <div className="grid-3">
        <div className="d-flex flex-column">
          <label>Date</label>
          <p className="text-big mt-2">{dayjs.utc(billableEvent.timestamp).format('MM/DD/YYYY')}</p>
        </div>
        <div className="d-flex flex-column">
          <label>Type</label>
          <p className="text-big mt-2">{typeToPrettyString(billableEvent.type)}</p>
        </div>
        <div className="d-flex flex-column">
          <label>Cost</label>
          <input
            type="number"
            step="any"
            min={0}
            className="mt-2"
            value={billableEvent.cost ?? 0}
            onChange={e => {
              setBillableEvent(partial => ({ ...partial, cost: Number(e.target.value) }));
              setCostInputValid(e.target.checkValidity());
            }}
          />
        </div>
        <div className="d-flex flex-column">
          <label>Associated Unit</label>
          <p className="text-big mt-2">{billableEvent.associatedUnit?.unitNumber ?? 'N/A'}</p>
        </div>
        <div className="d-flex flex-column">
          <label>Notes</label>
          <p className="text-big mt-2">{billableEvent.label}</p>
        </div>
        <div className="d-flex flex-column">
          <label>Operator</label>
          <p className="text-big mt-2">{billableEvent.operator}</p>
        </div>
      </div>
      <div className="d-flex align-items-center justify-content-between mt-3">
        <div>
          <button className="btn-back" onClick={() => setView(BillableEventView.LIST)}>
            <ArrowBackIos fontSize="small" />
            Back
          </button>
        </div>
        <div className="d-flex g-2">
          <button type="button" className="btn-outline" onClick={() => close()}>
            Cancel
          </button>
          <Loader loading={submitLoading} scale="50%">
            <button
              type="button"
              className="btn-primary"
              onClick={() => {
                setSubmitLoading(true);
                updateInvoiceItemBillableEvent({
                  invoiceItemId: invoiceItem.id,
                  billableEvent,
                  billableEventId: billableEvent.id
                })
                  .then(() => {
                    close();
                    onItemEditSuccess();
                  })
                  .finally(() => setSubmitLoading(false));
              }}
              disabled={!costInputValid}
            >
              Submit
            </button>
          </Loader>
        </div>
      </div>
    </form>
  );
};

const NewEventForm: FC<{
  billableEvent: BillableEvent;
  setBillableEvent: React.Dispatch<React.SetStateAction<BillableEvent>>;
  invoiceItem: InvoiceItem;
  setView: React.Dispatch<React.SetStateAction<BillableEventView>>;
  close: () => void;
  onItemEditSuccess: () => void;
  invoice: Invoice;
}> = ({ billableEvent, setBillableEvent, invoiceItem, close, setView, onItemEditSuccess, invoice }) => {
  const [contract, setContract] = useState<Contract | null>(null);
  const [formValid, setFormValid] = useState(true);
  const [assocUnitRequired, setAssocUnitRequired] = useState(false);
  const [assocUnitValid, setAssocUnitValid] = useState(false);
  const [assocUnitNumber, setAssocUnitNumber] = useState(billableEvent.associatedUnit?.unitNumber ?? '');
  const [assocUnitSearching, setAssocUnitSearching] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [saveError, setSaveError] = useState('');
  const { selectedLocation } = useContext(SelectedLocationContext);
  const formRef = useRef<HTMLFormElement>(null);

  const editBillableEvent = useCallback(
    (partial: Partial<BillableEvent>) => {
      setBillableEvent(p => ({ ...p, ...partial }));
      setFormValid((formRef.current?.checkValidity() ?? false) && (assocUnitRequired ? assocUnitValid : true));
    },
    [assocUnitRequired, assocUnitValid, setBillableEvent]
  );

  const updateDefaultCost = useCallback(() => {
    if (!contract) {
      return;
    }

    let cost = 0;
    switch (billableEvent.type) {
      case BillableEventType.LIFT_OFF:
        cost = invoiceItem.loaded ? contract.liftOffLoaded : contract.liftOffEmpty;
        break;
      case BillableEventType.LIFT_ON:
        cost = invoiceItem.loaded ? contract.liftOnLoaded : contract.liftOnEmpty;
        break;
      case BillableEventType.DIG:
        cost = invoiceItem.loaded ? contract.digLoaded : contract.digEmpty;
        break;
      case BillableEventType.CREDIT:
      case BillableEventType.OTHER:
      default:
        cost = 0;
    }
    editBillableEvent({ cost });
  }, [billableEvent.type, contract, editBillableEvent, invoiceItem.loaded]);

  // Load contract
  useEffect(() => {
    if (!selectedLocation) {
      return;
    }
    getContractForSizeType({
      sizeType: invoiceItem.sizeType,
      date: new Date(billableEvent.timestamp),
      customerId: invoice.customer.id,
      locationId: selectedLocation.id
    }).then(c => setContract(c));
  }, [billableEvent.timestamp, invoice.customer.id, invoiceItem.sizeType, invoiceItem.unitNumber, selectedLocation]);

  // Update default cost when contract state changes
  useEffect(() => {
    updateDefaultCost();
  }, [updateDefaultCost]);

  // Is associated unit required?
  useEffect(() => {
    const required = typesRequiringAssocUnit.includes(billableEvent.type);
    setAssocUnitRequired(required);
  }, [billableEvent.type]);

  // Debounced search for associated unit
  useEffect(() => {
    if (assocUnitNumber.length) {
      setAssocUnitSearching(true);
      const search = setTimeout(() => {
        searchByUnitNumber(assocUnitNumber, new Date(billableEvent.timestamp))
          .then(u => {
            editBillableEvent({ associatedUnit: u ?? undefined });
            setAssocUnitValid(!!u);
          })
          .finally(() => setAssocUnitSearching(false));
      }, 300);
      return () => clearTimeout(search);
    }
  }, [assocUnitNumber, billableEvent.timestamp, editBillableEvent]);

  const handleSubmit = useCallback<React.FormEventHandler<HTMLFormElement>>(
    e => {
      e.preventDefault();
      setSubmitLoading(true);
      setSaveError('');
      addInvoiceItemBillableEvent({
        invoiceId: invoice.id,
        unitId: invoiceItem.unitId,
        billableEvent: assocUnitRequired ? billableEvent : { ...billableEvent, associatedUnit: undefined }
      })
        .then(() => {
          close();
          onItemEditSuccess();
        })
        .catch(error =>
          /* the server's own reason, e.g. a split invoice whose units changed loaded status */
          setSaveError(
            error instanceof InvoiceEventSaveError
              ? error.message
              : 'Something went wrong saving the event. Please try again.'
          )
        )
        .finally(() => setSubmitLoading(false));
    },
    [assocUnitRequired, billableEvent, close, invoice.id, invoiceItem.unitId, onItemEditSuccess]
  );

  return (
    <form className="mb-3" ref={formRef} onSubmit={handleSubmit}>
      <div className="grid-3">
        <div className="d-flex flex-column">
          <label>Date</label>
          <div className="mt-2">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DesktopDatePicker
                sx={{ backgroundColor: 'var(--white)' }}
                slotProps={{
                  textField: { margin: 'none', required: true, size: 'small', className: 'w-100' }
                }}
                value={dayjs(billableEvent.timestamp ?? new Date()).utc()}
                onChange={val =>
                  setBillableEvent(e => ({
                    ...e,
                    timestamp: val?.toISOString() ?? dayjs.utc().startOf('day').toISOString()
                  }))
                }
                minDate={dayjs.utc(invoiceItem.gateInDate)}
                maxDate={dayjs.utc(invoice.toDate)}
              />
            </LocalizationProvider>
          </div>
        </div>
        <div className="d-flex flex-column">
          <label>Type</label>
          <select
            className="w-100 mt-2 bg-white"
            value={billableEvent.type}
            onChange={e => {
              editBillableEvent({
                type: e.target.value as BillableEventType
              });
              updateDefaultCost();
            }}
          >
            {Object.values(BillableEventType).map(value => {
              return (
                <option value={value} key={value}>
                  {typeToPrettyString(value)}
                </option>
              );
            })}
          </select>
        </div>
        <div className="d-flex flex-column">
          <label>Cost</label>
          <input
            type="number"
            step={'any'}
            min={0}
            className="mt-2 w-100"
            value={billableEvent.cost ?? 0}
            onChange={e => editBillableEvent({ cost: Number(e.target.value) })}
          />
        </div>
        <div className="d-flex flex-column">
          <label>Notes</label>
          <input
            className="mt-2 w-100"
            value={billableEvent.label}
            onChange={e => editBillableEvent({ label: e.target.value })}
          />
        </div>
        <div className="d-flex flex-column">
          <label>Operator</label>
          <input
            className="mt-2 w-100"
            value={billableEvent.operator}
            onChange={e => editBillableEvent({ operator: e.target.value })}
          />
        </div>
        {assocUnitRequired && (
          <>
            <div className="d-flex flex-column">
              <label className="required">Associated Unit</label>
              <input
                type="text"
                className="mt-2 w-100"
                value={assocUnitNumber}
                onChange={e => setAssocUnitNumber(e.target.value.toUpperCase())}
              />
              {(!assocUnitNumber.length || assocUnitSearching) && <p className="mt-2">&nbsp;</p>}
              {!!assocUnitNumber.length && !assocUnitSearching && (
                <p className={`${assocUnitValid ? 'text-green' : 'text-danger'} mt-2`}>
                  {assocUnitValid ? 'Valid Unit' : 'Invalid Unit'}
                </p>
              )}
            </div>
          </>
        )}
      </div>
      {!!saveError && (
        <p className="text-danger mt-3 mb-0" role="alert">
          {saveError}
        </p>
      )}
      <div className="d-flex align-items-center justify-content-between mt-3">
        <div>
          <button className="btn-back" onClick={() => setView(BillableEventView.LIST)}>
            <ArrowBackIos fontSize="small" />
            Back
          </button>
        </div>
        <div className="d-flex g-2">
          <button type="button" className="btn-outline" onClick={() => close()}>
            Cancel
          </button>
          <Loader loading={submitLoading} scale="50%">
            <button type="submit" className="btn-primary" disabled={!formValid}>
              Submit
            </button>
          </Loader>
        </div>
      </div>
    </form>
  );
};

export const BillableEventForm: FC<{
  close: () => void;
  setView: React.Dispatch<React.SetStateAction<BillableEventView>>;
  existingEvent?: BillableEvent;
  onItemEditSuccess: () => void;
  invoiceItem: InvoiceItem;
  invoice: Invoice;
}> = ({ close, setView, existingEvent, invoiceItem, onItemEditSuccess, invoice }) => {
  const [billableEvent, setBillableEvent] = useState<BillableEvent>(
    existingEvent ?? {
      ...defaultBillableEvent(),
      unitNumber: invoiceItem.unitNumber,
      timestamp: invoiceItem.gateOutDate?.toISOString() ?? invoice.toDate.toISOString()
    }
  );

  return (
    <>
      {existingEvent && (
        <EditExistingEventForm
          billableEvent={billableEvent}
          setBillableEvent={setBillableEvent}
          setView={setView}
          close={close}
          invoiceItem={invoiceItem}
          onItemEditSuccess={onItemEditSuccess}
        />
      )}
      {!existingEvent && (
        <NewEventForm
          billableEvent={billableEvent}
          setBillableEvent={setBillableEvent}
          invoiceItem={invoiceItem}
          close={close}
          onItemEditSuccess={onItemEditSuccess}
          setView={setView}
          invoice={invoice}
        />
      )}
    </>
  );
};
